import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import SafeChart from '../components/SafeChart';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareerPathwayPage = () => {
  const { user, hasModuleAccess } = useAuth();

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [config, setConfig] = useState(null);
  const [phasesData, setPhasesData] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseDetail, setPhaseDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [createPhaseDialog, setCreatePhaseDialog] = useState(false);
  const [createMilestoneDialog, setCreateMilestoneDialog] = useState(false);
  const [phaseDetailDialog, setPhaseDetailDialog] = useState(false);
  const [updateMilestoneDialog, setUpdateMilestoneDialog] = useState(false);

  // Forms
  const [newPhase, setNewPhase] = useState({ phase_name: '', description: '', start_date: '', target_completion_date: '' });
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', category: 'Performance', target_date: '' });
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneUpdate, setMilestoneUpdate] = useState({ status: '', progress_percentage: '', notes: '' });
  const [autoProgressResult, setAutoProgressResult] = useState(null);

  const canEdit = user?.role === 'Admin' || hasModuleAccess(PM.CAREER_PATHWAY, 'edit');
  const isLinkedViewer = user?.role === 'Player' || user?.role === 'Parent';

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Auto-select linked athlete for Player / Parent roles
  useEffect(() => {
    if (isLinkedViewer && athletes.length > 0 && !selectedAthlete) {
      setSelectedAthlete(athletes[0].id);
    }
  }, [isLinkedViewer, athletes, selectedAthlete]);

  // Phase 81 — pre-select athlete from ?athlete=<id> deep-link.
  // Set unconditionally; visibility filter may strip contract fixtures.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const aid = searchParams.get('athlete') || searchParams.get('athlete_id');
    if (aid) setSelectedAthlete(aid);
  }, [searchParams]);

  useEffect(() => {
    if (selectedAthlete) {
      // Reset stale data before loading new athlete
      setPhasesData(null);
      setSelectedPhase(null);
      setPhaseDetail(null);
      setAutoProgressResult(null);
      fetchAthletePhases(selectedAthlete);
    }
  }, [selectedAthlete]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [athletesRes, configRes] = await Promise.all([
        axios.get(`${API}/athletes`),
        axios.get(`${API}/career-v2/config`)
      ]);
      const athleteList = Array.isArray(athletesRes.data) ? athletesRes.data : (athletesRes.data.athletes || []);
      setAthletes(athleteList);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletePhases = useCallback(async (athleteId) => {
    try {
      const response = await axios.get(`${API}/career-v2/phases/athlete/${athleteId}`);
      setPhasesData(response.data);
    } catch (error) {
      toast.error('Failed to load career data');
      setPhasesData(null);
    }
  }, []);

  const openPhaseDetail = async (phaseId) => {
    try {
      const res = await axios.get(`${API}/career-v2/phases/${phaseId}`);
      setPhaseDetail(res.data);
      setPhaseDetailDialog(true);
    } catch {
      toast.error('Failed to load phase details');
    }
  };

  const handleCreatePhase = async () => {
    if (!newPhase.phase_name || !newPhase.start_date) {
      toast.error('Phase name and start date are required');
      return;
    }
    try {
      await axios.post(`${API}/career-v2/phases`, {
        athlete_id: selectedAthlete,
        ...newPhase,
        target_completion_date: newPhase.target_completion_date || null
      });
      toast.success('Career phase created');
      setCreatePhaseDialog(false);
      setNewPhase({ phase_name: '', description: '', start_date: '', target_completion_date: '' });
      fetchAthletePhases(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create phase');
    }
  };

  const handleCreateMilestone = async () => {
    if (!newMilestone.title || !selectedPhase) {
      toast.error('Title is required');
      return;
    }
    try {
      await axios.post(`${API}/career-v2/milestones`, {
        phase_id: selectedPhase.id,
        athlete_id: selectedAthlete,
        title: newMilestone.title,
        description: newMilestone.description || null,
        category: newMilestone.category,
        target_date: newMilestone.target_date || null
      });
      toast.success('Milestone created');
      setCreateMilestoneDialog(false);
      setNewMilestone({ title: '', description: '', category: 'Performance', target_date: '' });
      // Refresh phase detail
      openPhaseDetail(selectedPhase.id);
      fetchAthletePhases(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create milestone');
    }
  };

  const handleUpdateMilestone = async () => {
    if (!editingMilestone) return;
    try {
      const update = {};
      if (milestoneUpdate.status) update.status = milestoneUpdate.status;
      if (milestoneUpdate.progress_percentage !== '') update.progress_percentage = parseFloat(milestoneUpdate.progress_percentage);
      if (milestoneUpdate.notes) update.notes = milestoneUpdate.notes;

      await axios.put(`${API}/career-v2/milestones/${editingMilestone.id}`, update);
      toast.success('Milestone updated');
      setUpdateMilestoneDialog(false);
      setEditingMilestone(null);
      if (selectedPhase) openPhaseDetail(selectedPhase.id);
      fetchAthletePhases(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update milestone');
    }
  };

  const handleUpdatePhaseStatus = async (phaseId, newStatus) => {
    try {
      await axios.put(`${API}/career-v2/phases/${phaseId}`, { status: newStatus });
      toast.success(`Phase marked as ${newStatus}`);
      fetchAthletePhases(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phase');
    }
  };

  const openMilestoneUpdate = (milestone) => {
    setEditingMilestone(milestone);
    setMilestoneUpdate({
      status: milestone.status,
      progress_percentage: String(milestone.progress_percentage || 0),
      notes: milestone.notes || ''
    });
    setUpdateMilestoneDialog(true);
  };

  const handleAutoProgressCheck = async () => {
    try {
      const res = await axios.post(`${API}/career-v2/auto-progress/check/${selectedAthlete}`);
      setAutoProgressResult(res.data);
      if (res.data.actions?.length === 0 && res.data.eligible) {
        toast.info('All milestones are already unlocked');
      } else if (!res.data.eligible && !res.data.actions?.length) {
        toast.info('Criteria not met for auto-progression');
      }
    } catch (error) {
      toast.error('Failed to check auto-progression');
    }
  };

  const handleApplyAutoProgress = async (milestoneIds) => {
    try {
      await axios.post(`${API}/career-v2/auto-progress/apply`, milestoneIds);
      toast.success('Milestones unlocked');
      setAutoProgressResult(null);
      fetchAthletePhases(selectedAthlete);
      if (selectedPhase) openPhaseDetail(selectedPhase.id);
    } catch (error) {
      toast.error('Failed to apply progression');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-emerald-100 text-emerald-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      case 'Paused': return 'bg-amber-100 text-amber-800';
      case 'Upcoming': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
      case 'Locked': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
      case 'In Progress': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Performance': return 'bg-blue-100 text-blue-800';
      case 'Physical': return 'bg-red-100 text-red-800';
      case 'Tactical': return 'bg-purple-100 text-purple-800';
      case 'Mental': return 'bg-amber-100 text-amber-800';
      case 'Academic': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const phases = phasesData?.phases || [];
  const overallProgress = phasesData?.overall_progress;
  const careerScore = phasesData?.career_score;
  const integration = phasesData?.integration_data;

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="career-pathway-page">
        <div className="mb-6 flex items-center gap-3">
          <img src="/pfap-logo-icon.png" alt="PFAP" className="h-10 w-10 hidden sm:block" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Career Pathway
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1 text-sm">Track career development phases and milestones</p>
          </div>
        </div>

        {/* Athlete Selector — hidden for Player/Parent (auto-selected) */}
        {!isLinkedViewer && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1 max-w-md">
                  <SearchableAthleteSelect
                    athletes={athletes}
                    value={selectedAthlete}
                    onChange={setSelectedAthlete}
                    placeholder="Search & select athlete..."
                    label="Select Athlete"
                    data-testid="athlete-select"
                  />
                </div>
                {canEdit && selectedAthlete && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setCreatePhaseDialog(true)}
                      className="bg-pfap-green hover:bg-pfap-green/90"
                      data-testid="create-phase-btn"
                    >
                      + New Phase
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleAutoProgressCheck}
                      data-testid="auto-progress-btn"
                    >
                      Check Auto-Progress
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {selectedAthlete && phasesData ? (
          <>
            {/* Progress Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-emerald-600" data-testid="overall-progress">
                    {overallProgress?.overall_progress?.toFixed(0) || 0}%
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Overall Progress</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-blue-600" data-testid="total-phases">{overallProgress?.total_phases || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Total Phases</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-purple-600" data-testid="completed-phases">{overallProgress?.completed_phases || 0}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Completed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-amber-600" data-testid="active-phase">
                    {overallProgress?.active_phase || 'None'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Active Phase</p>
                </CardContent>
              </Card>
            </div>

            {/* Integration Data Summary */}
            {integration && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-500">Cross-Module Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6 flex-wrap text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-500">PDP Progress:</span>{' '}
                      <span className="font-semibold">{integration.pdp_progress || 0}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-500">Avg Match Rating:</span>{' '}
                      <span className="font-semibold">{integration.avg_match_rating || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-500">Matches Played:</span>{' '}
                      <span className="font-semibold">{integration.match_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-500">Active Injuries:</span>{' '}
                      <span className={`font-semibold ${integration.has_active_injury ? 'text-red-600' : 'text-emerald-600'}`}>
                        {integration.has_active_injury ? 'Yes' : 'None'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Auto-Progress Results */}
            {autoProgressResult && (
              <Card className="mb-6 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Auto-Progression Check</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {autoProgressResult.criteria && (
                      <div className="flex gap-4 flex-wrap text-sm">
                        <Badge className={autoProgressResult.criteria.pdp_met ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                          PDP: {autoProgressResult.criteria.pdp_progress}% {autoProgressResult.criteria.pdp_met ? '(Met)' : '(Need 70%)'}
                        </Badge>
                        <Badge className={autoProgressResult.criteria.rating_met ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                          Rating: {autoProgressResult.criteria.avg_rating} {autoProgressResult.criteria.rating_met ? '(Met)' : '(Need 6.5)'}
                        </Badge>
                        <Badge className={autoProgressResult.criteria.no_injury ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                          {autoProgressResult.criteria.no_injury ? 'No Injury' : 'Injury Active'}
                        </Badge>
                      </div>
                    )}
                    {autoProgressResult.actions?.length > 0 && (
                      <div className="space-y-2">
                        {autoProgressResult.actions.map(a => (
                          <div key={a.milestone_id} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                            <div>
                              <span className="text-sm font-medium">{a.milestone_title}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-500 ml-2">({a.reason})</span>
                            </div>
                            {a.type === 'unlock' && (
                              <Button
                                size="sm"
                                onClick={() => handleApplyAutoProgress([a.milestone_id])}
                                className="bg-pfap-green hover:bg-pfap-green/90"
                                data-testid={`unlock-milestone-${a.milestone_id}`}
                              >
                                Unlock
                              </Button>
                            )}
                            {a.type === 'pause_suggested' && (
                              <Badge className="bg-amber-100 text-amber-800">Pause Suggested</Badge>
                            )}
                          </div>
                        ))}
                        {autoProgressResult.actions.filter(a => a.type === 'unlock').length > 1 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApplyAutoProgress(autoProgressResult.actions.filter(a => a.type === 'unlock').map(a => a.milestone_id))}
                          >
                            Unlock All ({autoProgressResult.actions.filter(a => a.type === 'unlock').length})
                          </Button>
                        )}
                      </div>
                    )}
                    {autoProgressResult.actions?.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-500">{autoProgressResult.message}</p>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setAutoProgressResult(null)}>Dismiss</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phase Progress Chart */}
            {phases.length >= 1 && (
              <Card className="mb-6" data-testid="career-phase-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-500">Phase & Milestone Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <SafeChart height={200}>
                    <BarChart data={phases.map(p => ({
                      name: p.phase_name?.length > 12 ? p.phase_name.substring(0, 12) + '...' : (p.phase_name || ''),
                      progress: p.completion_percentage || 0,
                      milestones: ((p.milestones_completed || 0) / Math.max(p.milestones_count || 1, 1)) * 100
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{fontSize: 11}} />
                      <YAxis domain={[0, 100]} tick={{fontSize: 11}} />
                      <Tooltip formatter={(v) => [`${Number(v || 0).toFixed(0)}%`]} />
                      <Bar dataKey="progress" fill="#059669" radius={[4, 4, 0, 0]} name="Phase Progress" />
                      <Bar dataKey="milestones" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Milestone %" />
                    </BarChart>
                  </SafeChart>
                </CardContent>
              </Card>
            )}

            {/* Phases List */}
            <Card>
              <CardHeader>
                <CardTitle>Career Phases</CardTitle>
                <CardDescription>{phases.length} phases defined</CardDescription>
              </CardHeader>
              <CardContent>
                {phases.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-500" data-testid="no-phases">
                    No career phases defined.{canEdit && ' Click "+ New Phase" to start building the pathway.'}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {phases.map((phase, index) => (
                      <div
                        key={phase.id}
                        className="border rounded-lg p-4 hover:border-emerald-300 transition-colors cursor-pointer"
                        onClick={() => { setSelectedPhase(phase); openPhaseDetail(phase.id); }}
                        data-testid={`phase-row-${phase.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </div>
                            <div>
                              <span className="font-semibold text-gray-900 dark:text-gray-100">{phase.phase_name}</span>
                              <Badge className={`ml-2 ${getStatusColor(phase.status)}`}>{phase.status}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500 dark:text-gray-500">
                              {phase.milestones_completed || 0}/{phase.milestones_count || 0} milestones
                            </span>
                            {canEdit && phase.status === 'Active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleUpdatePhaseStatus(phase.id, 'Completed'); }}
                                data-testid={`complete-phase-${phase.id}`}
                              >
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                        {phase.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 ml-10">{phase.description}</p>
                        )}
                        <div className="ml-10 mt-2">
                          <Progress value={phase.completion_percentage || 0} className="h-2" />
                          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
                            <span>{phase.start_date}</span>
                            <span>{(phase.completion_percentage || 0).toFixed(0)}%</span>
                            <span>{phase.target_completion_date || phase.end_date || 'Ongoing'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : selectedAthlete ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              Loading career data...
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              {isLinkedViewer
                ? (user?.role === 'Parent'
                    ? 'No career phases yet — your child\'s coach will add development phases here.'
                    : 'No career phases yet — your coach will add development phases here.')
                : 'Select an athlete to view their career pathway'}
            </CardContent>
          </Card>
        )}

        {/* ===== CREATE PHASE DIALOG ===== */}
        <Dialog open={createPhaseDialog} onOpenChange={setCreatePhaseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Career Phase</DialogTitle>
              <DialogDescription>Define a new development phase for this athlete</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Phase Name *</Label>
                <Select value={newPhase.phase_name} onValueChange={v => setNewPhase(p => ({ ...p, phase_name: v }))}>
                  <SelectTrigger data-testid="phase-name-select">
                    <SelectValue placeholder="Select phase..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.phases || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newPhase.description} onChange={e => setNewPhase(p => ({ ...p, description: e.target.value }))} placeholder="Describe the phase goals..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input type="date" value={newPhase.start_date} onChange={e => setNewPhase(p => ({ ...p, start_date: e.target.value }))} data-testid="phase-start-date" />
                </div>
                <div>
                  <Label>Target Completion</Label>
                  <Input type="date" value={newPhase.target_completion_date} onChange={e => setNewPhase(p => ({ ...p, target_completion_date: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatePhaseDialog(false)}>Cancel</Button>
              <Button onClick={handleCreatePhase} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="save-phase-btn">Create Phase</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== PHASE DETAIL DIALOG ===== */}
        <Dialog open={phaseDetailDialog} onOpenChange={setPhaseDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{phaseDetail?.phase?.phase_name}</DialogTitle>
              <DialogDescription>
                {phaseDetail?.phase?.description || 'Career development phase'}
              </DialogDescription>
            </DialogHeader>
            {phaseDetail && (
              <div className="space-y-4" data-testid="phase-detail">
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(phaseDetail.phase.status)}>{phaseDetail.phase.status}</Badge>
                  <span className="text-sm text-gray-500 dark:text-gray-500">Progress: {(phaseDetail.phase.completion_percentage || 0).toFixed(0)}%</span>
                </div>
                <Progress value={phaseDetail.phase.completion_percentage || 0} className="h-2" />

                {/* Milestones */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium">Milestones ({phaseDetail.milestones?.length || 0})</p>
                    {canEdit && (
                      <Button
                        size="sm"
                        onClick={() => { setSelectedPhase(phaseDetail.phase); setCreateMilestoneDialog(true); }}
                        className="bg-pfap-green hover:bg-pfap-green/90"
                        data-testid="add-milestone-btn"
                      >
                        + Add Milestone
                      </Button>
                    )}
                  </div>

                  {(!phaseDetail.milestones || phaseDetail.milestones.length === 0) ? (
                    <p className="text-sm text-gray-500 dark:text-gray-500 text-center py-4">No milestones yet. Add milestones to track progress.</p>
                  ) : (
                    <div className="space-y-2">
                      {phaseDetail.milestones.map(m => (
                        <div
                          key={m.id}
                          className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg flex items-center justify-between"
                          data-testid={`milestone-${m.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{m.title}</span>
                              <Badge className={getStatusColor(m.status)} variant="outline">{m.status}</Badge>
                              <Badge className={getCategoryColor(m.category)} variant="outline">{m.category}</Badge>
                            </div>
                            {m.description && <p className="text-xs text-gray-500 dark:text-gray-500">{m.description}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={m.progress_percentage || 0} className="h-1.5 flex-1 max-w-[200px]" />
                              <span className="text-xs text-gray-400 dark:text-gray-500">{(m.progress_percentage || 0).toFixed(0)}%</span>
                            </div>
                            {m.notes && <p className="text-xs text-amber-600 mt-1">{m.notes}</p>}
                          </div>
                          {canEdit && m.status !== 'Completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openMilestoneUpdate(m)}
                              data-testid={`update-milestone-${m.id}`}
                            >
                              Update
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Evaluations */}
                {phaseDetail.evaluations?.length > 0 && (
                  <div className="border-t pt-4">
                    <p className="font-medium mb-2">Coach Evaluations</p>
                    <div className="space-y-2">
                      {phaseDetail.evaluations.map(ev => (
                        <div key={ev.id} className="p-3 bg-blue-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={ev.readiness_rating >= 7 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                              Readiness: {ev.readiness_rating}/10
                            </Badge>
                            <Badge className={ev.promotion_recommendation ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500'}>
                              {ev.promotion_recommendation ? 'Recommended for Promotion' : 'Not Ready Yet'}
                            </Badge>
                          </div>
                          {ev.strengths && <p className="text-xs text-gray-600 dark:text-gray-500">Strengths: {ev.strengths}</p>}
                          {ev.areas_to_develop && <p className="text-xs text-gray-600 dark:text-gray-500">Develop: {ev.areas_to_develop}</p>}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">By {ev.coach_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== CREATE MILESTONE DIALOG ===== */}
        <Dialog open={createMilestoneDialog} onOpenChange={setCreateMilestoneDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Milestone</DialogTitle>
              <DialogDescription>Add a milestone to {selectedPhase?.phase_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={newMilestone.title} onChange={e => setNewMilestone(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Achieve 80% passing accuracy" data-testid="milestone-title-input" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newMilestone.category} onValueChange={v => setNewMilestone(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="milestone-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.milestone_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newMilestone.description} onChange={e => setNewMilestone(p => ({ ...p, description: e.target.value }))} placeholder="Milestone details..." />
              </div>
              <div>
                <Label>Target Date</Label>
                <Input type="date" value={newMilestone.target_date} onChange={e => setNewMilestone(p => ({ ...p, target_date: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateMilestoneDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateMilestone} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="save-milestone-btn">Add Milestone</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== UPDATE MILESTONE DIALOG ===== */}
        <Dialog open={updateMilestoneDialog} onOpenChange={setUpdateMilestoneDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Milestone</DialogTitle>
              <DialogDescription>{editingMilestone?.title}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select value={milestoneUpdate.status} onValueChange={v => setMilestoneUpdate(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="milestone-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(config?.milestone_statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Progress (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={milestoneUpdate.progress_percentage}
                  onChange={e => setMilestoneUpdate(p => ({ ...p, progress_percentage: e.target.value }))}
                  data-testid="milestone-progress-input"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={milestoneUpdate.notes} onChange={e => setMilestoneUpdate(p => ({ ...p, notes: e.target.value }))} placeholder="Update notes..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpdateMilestoneDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateMilestone} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="update-milestone-btn">Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CareerPathwayPage;
